/**
 * Client utilities for the pdf-ms service (HTML → PDF).
 * Request/response shapes match the pdf-ms API.
 */

/** Endpoint path for HTML-to-PDF conversion (append to baseUrl). */
export const PDF_MS_ENDPOINT = '/api/pdf';

/**
 * Options for PDF generation, passed to pdf-ms.
 * Keys must match what the pdf-ms API accepts.
 */
export interface PdfMsOptions {
  format?: string;
  orientation?: 'portrait' | 'landscape';
  marginTop?: string;
  marginRight?: string;
  marginBottom?: string;
  marginLeft?: string;
  width?: string;
  height?: string;
}

/** Response body from pdf-ms POST /api/pdf. */
export interface PdfMsResponse {
  pdf: string;
}

/**
 * Calls the pdf-ms service to convert HTML to PDF.
 * Uses the provided `post` function so callers can use their own HTTP client (axios, requestor, etc.).
 *
 * @param baseUrl - Base URL of pdf-ms (e.g. http://pdf-ms:8080)
 * @param html - HTML string to convert
 * @param options - Optional PDF options (format, orientation, margins, etc.)
 * @param post - Function that POSTs JSON to url and returns response data { pdf: string }
 * @returns Decoded PDF as Buffer
 * @example
 * const buffer = await htmlToPdfFromService(
 *   'http://pdf-ms:8080',
 *   '<html><body>Hello</body></html>',
 *   { format: 'A4' },
 *   (url, body) => axios.post(url, body).then(r => r.data)
 * );
 */
export async function htmlToPdfFromService(
  baseUrl: string,
  html: string,
  options: PdfMsOptions | undefined,
  post: (url: string, body: object) => Promise<PdfMsResponse>
): Promise<Buffer> {
  const url = `${baseUrl.replace(/\/$/, '')}${PDF_MS_ENDPOINT}`;
  const body = { html, options: options ?? {} };
  const response = await post(url, body);
  if (!response?.pdf) {
    throw new Error('pdf-ms response missing pdf field');
  }
  return Buffer.from(response.pdf, 'base64');
}
